import { apolloClient } from '@/services/apollo-client';
import type { AiSerializedMessage } from 'juneau';
import {
  CancelAiChatProposalDocument,
  ClearChatHistoryDocument,
  ConfirmAiChatProposalDocument,
  GetChatHistoryDocument,
  SaveChatHistoryDocument
} from '@/graphql';

export type { AiSerializedMessage };
import type { ProposalSecret } from './proposal-secrets';

/** Cross-tab notification channel — one history row per user, so tabs must coordinate. */
export const HISTORY_CHANNEL = 'ai-chat-history';

/** Notifies other tabs that the server-side history changed (saved or cleared). */
function broadcastHistoryChange(): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(HISTORY_CHANNEL);
  channel.postMessage('saved');
  channel.close();
}

/** Loads the persisted chat history. Throws on network/GraphQL failure — callers decide how to react. */
export async function fetchChatHistory(): Promise<AiSerializedMessage[]> {
  const { data } = await apolloClient.query({
    query: GetChatHistoryDocument,
    fetchPolicy: 'network-only'
  });
  if (!data) return [];
  // Explicit field picking strips __typename and any extra props; role is a plain
  // String in the schema — safe single cast back to the juneau role union.
  return data.chatHistory.map(m => ({
    id: m.id,
    role: m.role as AiSerializedMessage['role'],
    parts: m.parts as AiSerializedMessage['parts'],
    createdAt: m.createdAt
  }));
}

export async function saveChatHistory(messages: AiSerializedMessage[]): Promise<void> {
  await apolloClient.mutate({
    mutation: SaveChatHistoryDocument,
    variables: {
      input: {
        // Pick only the fields SaveChatHistoryInput accepts (drops __typename / `v`).
        messages: messages.map(m => ({
          id: m.id,
          role: m.role,
          parts: m.parts,
          createdAt: m.createdAt
        }))
      }
    }
  });
}

export async function clearChatHistory(): Promise<void> {
  await apolloClient.mutate({ mutation: ClearChatHistoryDocument });
  // Idle tabs re-fetch and drop the cleared conversation instead of keeping it stale.
  broadcastHistoryChange();
}

export interface ProposalResult {
  success: boolean;
  message: string | null;
}

export async function confirmProposal(
  proposalId: string,
  secrets: readonly ProposalSecret[] = []
): Promise<ProposalResult> {
  const { data } = await apolloClient.mutate({
    mutation: ConfirmAiChatProposalDocument,
    variables: {
      input: {
        proposalId,
        ...(secrets.length > 0 && { secrets: secrets.map(({ key, value }) => ({ key, value })) })
      }
    }
  });
  return {
    success: data?.confirmAiChatProposal.success ?? false,
    message: data?.confirmAiChatProposal.message ?? null
  };
}

export async function cancelProposal(proposalId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: CancelAiChatProposalDocument,
    variables: { input: { proposalId } }
  });
}
